'use client'

import { useCallback, useEffect, useState } from 'react'

import { arrow, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom'

import {
  PORTAL_ONBOARDING_LAYOUT,
  PORTAL_ONBOARDING_STEPS,
} from '@common/apps/portal-onboarding/portal-onboarding.const'

import { usePortalOnboardingTargetElement, usePortalOnboardingTargetRect } from '@apps/portal-onboarding/hooks'
import type { PortalOnboardingStepId } from '@apps/portal-onboarding/portal-onboarding.const'
import { getPortalOnboardingDomTarget } from '@apps/portal-onboarding/portal-onboarding.utils'
import { usePortalOnboardingStore } from '@apps/portal-onboarding/stores/portal-onboarding.store'

import { usePortalOnboardingDismiss } from './usePortalTutorialDismiss'

export function usePortalOnboardingStepView() {
  const { currentStepIndex, nextStep, previousStep, complete } = usePortalOnboardingStore()
  const handleDismiss = usePortalOnboardingDismiss()

  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null)
  const [floatingElement, setFloatingElement] = useState<HTMLDivElement | null>(null)

  const currentStep: PortalOnboardingStepId | null = PORTAL_ONBOARDING_STEPS[currentStepIndex] ?? null
  const domTarget = currentStep ? getPortalOnboardingDomTarget(currentStep) : null
  const targetRect = usePortalOnboardingTargetRect(domTarget, true)
  const { element: targetElement, status: targetStatus } = usePortalOnboardingTargetElement(domTarget, true)

  const { floatingStyles, placement, middlewareData, isPositioned } = useFloating<HTMLElement>({
    placement: 'right',
    strategy: 'fixed',
    elements: { reference: targetElement, floating: floatingElement },
    middleware: [
      offset(PORTAL_ONBOARDING_LAYOUT.popoverOffset),
      flip({ padding: PORTAL_ONBOARDING_LAYOUT.viewportPadding }),
      shift({ padding: PORTAL_ONBOARDING_LAYOUT.viewportPadding }),
      arrow({ element: arrowElement, padding: PORTAL_ONBOARDING_LAYOUT.viewportPadding }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const pointerSide = placement.split('-')[0] as keyof typeof PORTAL_ONBOARDING_LAYOUT.pointerStaticSide
  const isLastStep = currentStepIndex === PORTAL_ONBOARDING_STEPS.length - 1

  useEffect(() => {
    if (targetStatus !== 'missing') return

    if (isLastStep) {
      complete()
      return
    }

    nextStep()
  }, [complete, isLastStep, nextStep, targetStatus])

  const handleFinish = useCallback(() => {
    if (isLastStep) {
      complete()
      return
    }

    nextStep()
  }, [complete, isLastStep, nextStep])

  return {
    currentStep,
    currentStepIndex,
    isLastStep,
    targetRect,
    targetElement,
    floatingStyles,
    isPositioned,
    pointerSide,
    arrowData: middlewareData.arrow,
    setArrowElement,
    setFloatingElement,
    handleDismiss,
    previousStep,
    handleFinish,
  }
}
